import React from 'react';
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";

interface LinkedIncidentsTableProps {
  incidentsByApi: [string, any[]][];
  catalogTasks?: any[];
}

export function LinkedIncidentsTable({ incidentsByApi, catalogTasks = [] }: LinkedIncidentsTableProps) {
  // Process and combine catalog tasks with incidents
  const processedData = React.useMemo(() => {
    const apiMap = new Map<string, {
      incidents: any[];
      deploymentDate: string | null;
      description: string;
    }>();

    // First, process catalog tasks to get deployment dates
    catalogTasks?.forEach(task => {
      const description = task.short_description || task.description || "";
      // Only include tasks that are deployment-related
      if (description.toLowerCase().includes('deploy')) {
        const apiName = task.api_name || "Unknown API";
        const deploymentDate = task.opened || task.created || task.date;
        
        if (!apiMap.has(apiName)) {
          apiMap.set(apiName, {
            incidents: [],
            deploymentDate: deploymentDate,
            description
          });
        }
      }
    });

    // Then add incidents to their respective APIs
    incidentsByApi.forEach(([apiName, incidents]) => {
      if (apiMap.has(apiName)) {
        apiMap.get(apiName)!.incidents = incidents;
      } else {
        // If we have incidents but no catalog task, create an entry
        const firstIncident = incidents[0];
        apiMap.set(apiName, {
          incidents,
          deploymentDate: firstIncident?.catalog_task_date || null,
          description: ""
        });
      }
    });

    // Convert to array and sort by deployment date
    return Array.from(apiMap.entries())
      .sort((a, b) => {
        const dateA = a[1].deploymentDate ? new Date(a[1].deploymentDate).getTime() : 0;
        const dateB = b[1].deploymentDate ? new Date(b[1].deploymentDate).getTime() : 0;
        return dateA - dateB; // ascending order
      });
  }, [incidentsByApi, catalogTasks]);

  if (!processedData.length) {
    return (
      <div className="text-sm text-muted-foreground">
        No linked incidents found.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {processedData.map(([apiName, data]) => {
        const deploymentDate = data.deploymentDate
          ? format(new Date(data.deploymentDate), 'MMM d, yyyy')
          : "Unknown";

        // Deduplicate incidents
        const uniqueIncidents = data.incidents.reduce((acc: any[], incident: any) => {
          if (!acc.some(i => i.incident_number === incident.incident_number)) {
            acc.push(incident);
          }
          return acc;
        }, []);

        return (
          <div key={apiName} className="border rounded-lg p-4">
            <div className="flex flex-col gap-2">
              <div className="text-base font-medium text-left">
                Deployment Date: <span className="font-semibold text-primary">{deploymentDate}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-base">{apiName}</span>
                <Badge variant="outline">{uniqueIncidents.length} incidents</Badge>
              </div>
            </div>

            {uniqueIncidents.length > 0 && (
              <Collapsible className="mt-4">
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full flex justify-between p-2">
                    <span>View Incidents</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="rounded-md border">
                    <Accordion type="multiple">
                      {uniqueIncidents.map((incident: any, idx: number) => (
                        <AccordionItem value={String(idx)} key={incident.incident_number} className="border-b last:border-b-0">
                          <AccordionTrigger className="w-full px-4 py-2 text-left hover:bg-muted/50">
                            <div className="grid grid-cols-4 md:grid-cols-5 gap-2 w-full items-center">
                              <div className="col-span-1">
                                <span className="text-xs text-muted-foreground block md:hidden">Incident #</span>
                                <span>{incident.incident_number || "—"}</span>
                              </div>
                              <div className="col-span-1 hidden md:block">
                                <Badge variant="outline" className="capitalize text-xs">
                                  {incident.priority || "Unknown"}
                                </Badge>
                              </div>
                              <div className="col-span-1 hidden md:block">
                                <Badge variant="outline" className="capitalize text-xs">
                                  {incident.status || "Unknown"}
                                </Badge>
                              </div>
                              <div className="col-span-2 md:col-span-2 truncate">
                                {incident.incident_created_date
                                  ? format(new Date(incident.incident_created_date), 'MMM d, yyyy h:mm a')
                                  : "Unknown"}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="bg-muted/50 px-4 py-3">
                            <div>
                              <span className="font-semibold">Description: </span>
                              <span>{incident.incident_description || "No description"}</span>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        );
      })}
    </div>
  );
}
